/* import __COLOCATED_TEMPLATE__ from './topics-explorer.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from 'tracked-built-ins';
import type Range from 'embercom/models/reporting/range';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import { fetchTopicsTaxonomy } from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import type {
  Topic,
  TopicTaxonomy,
} from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import { inject as service } from '@ember/service';
import MetricPickerItem from './topics-explorer/metric-picker-item';
import { metricConfigs } from './topics-explorer/common';
import type IntlService from 'ember-intl/services/intl';
import { insightsMetrics } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import { type MetricTableJob } from '../../ai-insights/fin-insights/metrics-table';
import { and } from '../../ai-insights/fin-insights/sankey-config';
import { cached } from 'tracked-toolbox';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import ENV from 'embercom/config/environment';
import { isBlank, isPresent } from '@ember/utils';

import { getAllowedFilterableAttributeIdsForStandalone } from 'embercom/services/reporting-standalone';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import type RouterService from '@ember/routing/router-service';
import QueryParams from 'embercom/resources/utils/query-params';
import type ReportingMetricsService from 'embercom/services/reporting-metrics';
import { type StandalonePlatform } from 'embercom/objects/standalone/constants';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { isRecentAiInsightsPurchase } from 'embercom/components/reporting/automation/ai-insights/lib/is-recent-ai-insights-purchase';

import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';

interface Args {
  conversationAttributeDescriptors: ConversationAttributeDescriptor[];
}

const ALL_TOPIC_LIMIT = Number.POSITIVE_INFINITY;

export default class TopicsExplorer extends Component<Args> {
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare reportingChartService: ReportingChartService;
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare permissionsService: any;
  @service declare router: RouterService;
  @service declare reportingMetrics: ReportingMetricsService;

  @tracked topicLimit = 10;
  @tracked selectedMetricId = Object.keys(this.metricConfigs)[0];
  @tracked showStandaloneSideDrawer = false;

  @tracked exploreConversationsState: {
    selectedTopic: Topic;
    selectedSubTopic: string | null;
    filters: LogicalFilter;
    dateRange: Range;
    conversationCount: number;
  } | null = null;

  @tracked hoveredTopicId: string | null = null;

  @use queryParams = QueryParams(this);
  @use topicsTaxonomy = AsyncData<TopicTaxonomy>(({ abortSignal }) =>
    fetchTopicsTaxonomy(
      this.appService.app.id,
      this.reportingChartService.syntheticDataUseCase,
      abortSignal,
    ),
  );

  @use topicConversationsCheck = staticDataResource(this, {
    serializedChart: () => ({
      visualization_type: 'counter',
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'exists',
            data: {
              property: 'conversation_custom_fields#ai_insights_topic',
              attribute: 'conversation_custom_fields#ai_insights_topic',
            },
          },
        },
      ],
      date_range: {
        selection: 'past_4_weeks',
      },
    }),
  });

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.topicsTaxonomy.isLoading; // this is a hack to eagerly load the topics taxonomy
  }

  get isDataAvailable(): boolean {
    return this.topicConversationsCheck?.hasData ?? false;
  }

  get shouldShowPostPurchaseEmptyState(): boolean {
    if (!isRecentAiInsightsPurchase(this.appService.app.products)) {
      return false;
    }
    return !this.topicConversationsCheck.isLoading && !this.isDataAvailable;
  }

  get isLoadingPostPurchase(): boolean {
    if (!isRecentAiInsightsPurchase(this.appService.app.products)) {
      return false;
    }
    return this.topicConversationsCheck.isLoading;
  }

  get selectedTopicId(): string | null {
    return this.queryParams.topicId || null;
  }

  @cached
  get metricConfigs() {
    return { ...metricConfigs(this.intl) };
  }

  get filterableProperties() {
    if (this.appService.app.isStandaloneApp) {
      return getAllowedFilterableAttributeIdsForStandalone(this.appService);
    }

    return this.reportingMetrics
      .getFilterableAttributesForMetrics(Object.keys(this.metricConfigs))
      .map(({ id }) => id)
      .filter((id) => id !== 'conversation.topic')
      .concat('teammate', 'team');
  }

  get showingAllTopics() {
    return this.topicLimit === ALL_TOPIC_LIMIT;
  }

  get topicLimitOptions() {
    let labelForCount = (count: number) =>
      this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topics-explorer.top-topics-by-volume',
        { count },
      );
    return [
      { text: labelForCount(10), value: 10 },
      { text: labelForCount(20), value: 20 },
      {
        text: this.intl.t(
          'components.reporting.automation.fin-insights.analyse.topics-explorer.top-topics-all',
        ),
        value: ALL_TOPIC_LIMIT,
      },
    ];
  }

  get metricOptions() {
    return Object.entries(this.metricConfigs).map(([metricId, metricConfig]) => ({
      text: metricConfig.longLabel ?? metricConfig.label,
      value: metricId,
      tooltipText: metricConfig.tooltip,
      component: MetricPickerItem,
      componentAttrs: { color: metricConfig.metricPickerItemClass },
    }));
  }

  get selectedTopic() {
    if (!this.selectedTopicId || this.topicsTaxonomy.isLoading) {
      return null;
    }

    return (
      this.topicsTaxonomy.value![this.selectedTopicId] ?? {
        name: this.selectedTopicId,
      }
    );
  }

  get metricsTableJob(): MetricTableJob {
    let highlightedMetricIndex =
      Object.keys(this.metricConfigs).findIndex((metricId) => metricId === this.selectedMetricId) +
      1;
    let metrics = insightsMetrics(this.intl);
    return {
      id: 'metrics-table',
      showSuggestions: false,
      table: {
        cta: 'components.reporting.automation.fin-insights.analyse.topics-explorer.table-cta',
        filters: and(...[]),
        chartSeries: [
          metrics.volume,
          metrics.aiCsatScore,
          metrics.involvementRate,
          metrics.resolutionRate,
          metrics.automationRate,
          metrics.handlingTimeMedian,
          metrics.firstResponseTime,
        ],
        highlightedMetricIndex,
      },
    };
  }

  get shouldShowZendeskConnectScreen() {
    if (!this.appService.app.isStandaloneApp || this.reportingChartService.useSyntheticData) {
      return false;
    }

    if (
      this.appService.app.hasStandalonePlatform('zendesk') &&
      !this.appService.mainStandalonePlatform.isConnected
    ) {
      return true;
    }

    return (
      this.appService.app.hasStandalonePlatform('zendesk') &&
      this.appService.mainStandalonePlatform.showAnalyzeSectionConnectCta
    );
  }

  get shouldShowSalesforceConnectScreen() {
    if (!this.appService.app.isStandaloneApp || this.reportingChartService.useSyntheticData) {
      return false;
    }

    if (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      !this.appService.app.canUseFeature('asana-skip-frontend-sfdc-integration-check') &&
      !this.appService.mainStandalonePlatform.isConnected
    ) {
      return true;
    }

    return (
      this.appService.app.hasStandalonePlatform('salesforce') &&
      this.appService.mainStandalonePlatform.showAnalyzeSectionConnectCta
    );
  }

  @action
  closeStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = false;
  }

  @action
  openStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = true;
  }

  get currentPlatform(): StandalonePlatform {
    return this.appService.mainStandalonePlatform.platformKey;
  }

  @action
  setSelectedTopicNull() {
    this.router.transitionTo(this.router.currentRouteName, {
      queryParams: { topicId: null },
    });
    this.trackAnalyticsEvent('navigated-subtopic-back-to-topic', 'topic-explorer');
  }

  get title() {
    if (this.selectedTopicId) {
      return this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topics-explorer.treemap.topics-count-with-back',
        { count: this.topicLimit },
      );
    } else {
      return this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topics-explorer.treemap.topics-count',
        { count: this.topicLimit },
      );
    }
  }

  @action
  async onShowConversationExplorer(
    filters: LogicalFilter,
    topic: string,
    subtopic: string | null,
    dateRange: Range,
    conversationCount: number,
  ) {
    if (this.topicsTaxonomy.isLoading) {
      await this.topicsTaxonomy.promise;
    }
    if (this.canAccessDrillIn) {
      let topicObject = this.topicsTaxonomy.value?.[topic] ?? {
        name: topic,
        description: null,
        subtopics: {},
      };
      this.exploreConversationsState = {
        selectedTopic: topicObject,
        selectedSubTopic: subtopic,
        filters,
        dateRange,
        conversationCount,
      };
      this.trackAnalyticsEvent('show-conversation-explorer', 'topics-explorer', {
        topic,
        subtopic,
        filters,
        dateRange,
        conversationCount,
      });
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
      this.trackAnalyticsEvent('show-conversation-explorer-no-permission', 'topics-explorer');
    }
  }

  get canAccessDrillIn() {
    return this.permissionsService.currentAdminCan('can_reporting__drillin__access');
  }

  get hasFinLive() {
    if (ENV.environment === 'development') {
      return true;
    } else if (this.appService.app.isStandaloneApp) {
      return this.appService.mainStandalonePlatform.finIsLive;
    }
    return this.aiAgentSetupService.isFinLiveIncludingChannelsOutsideSetup();
  }

  get emptyState() {
    if (this.appService.app.canUseFeature('insights-override-fin-live-empty-states')) {
      return null;
    }
    if (!this.hasFinLive) {
      return 'fin-not-live';
    } else {
      return null; // not an empty state - show it!
    }
  }

  get finIsNotLiveAndIsPaused() {
    if (ENV.environment === 'development' || !this.appService.app.isStandaloneApp) {
      return false;
    }
    return this.appService.mainStandalonePlatform.finIsNotLiveAndIsPaused;
  }

  get showHeader() {
    return (
      isBlank(this.emptyState) &&
      isPresent(this.topicsTaxonomy.value) &&
      !this.topicsTaxonomy.isLoading
    );
  }

  @action
  async onShowTableConversationExplorer(
    dateRange: Range,
    filters: LogicalFilter,
    topic: string,
    subtopic: string | null,
    _type: 'suggestions' | 'conversations',
    conversationCount: number,
  ) {
    this.onShowConversationExplorer(filters, topic, subtopic, dateRange, conversationCount);
  }

  @action
  closeConversationExplorer() {
    this.exploreConversationsState = null;
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/topics-explorer',
      action,
      object,
      ...extraContext,
    });
  }

  @action
  changeMetric(metricId: string) {
    this.selectedMetricId = metricId;
    this.trackAnalyticsEvent('metric-changed', 'topic-explorer', {
      metricId,
    });
  }

  @action
  changeTopicLimit(topicLimit: number) {
    this.topicLimit = topicLimit;
    this.trackAnalyticsEvent('top-topic-count-changed', 'topic-explorer', {
      topicLimit: this.topicLimit === ALL_TOPIC_LIMIT ? 'all' : this.topicLimit,
    });
  }

  @action
  topicSelected(topicId: string) {
    this.router.transitionTo(this.router.currentRouteName, {
      queryParams: { topicId },
    });
    this.trackAnalyticsEvent('topic-selected', 'topic-explorer', {
      topicId,
    });
  }

  @action
  onTopicHovered(topicId: string | null) {
    this.hoveredTopicId = topicId;
  }

  @action
  navigateToTopicsSettings() {
    let url = this.router.urlFor('apps.app.settings.data.topics');
    safeWindowOpen(url, '_blank');
    this.trackAnalyticsEvent('navigate-to-topics-settings', 'topic-explorer');
  }

  get serializedReport() {
    return {
      id: 'topics-explorer',
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicsExplorer': typeof TopicsExplorer;
  }
}
